import {
  Box,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import {useMemo, useState, useEffect} from 'react';
// @ts-ignore -- tz-lookup ships no types
import tzlookup from 'tz-lookup';
// import {apartmentServiceCreate, apartmentServiceUpdate} from 'src/apis';
// import {Apartment, GeoLocation, IdRsp} from 'src/apis/autoGenerate';
import {apartmentServiceCreate, apartmentServiceUpdate} from 'src/apis/autoGenerate/services';
import {Apartment, PropertyType} from 'src/apis/autoGenerate/types/apartment';
import {GeoLocation} from 'src/apis/autoGenerate/types/geoLocation';
import {IdRsp} from 'src/apis/autoGenerate/common';
import message from 'src/components/Message';
import Modal from 'src/components/Modal';
import {StateSelect} from 'src/components/StateSelect';
import {useAppSelector} from 'src/redux/hooks';
import {selectAccount} from 'src/redux/slice/accountSlice';
import PlacesAutocomplete, {PlaceType} from './PlacesAutocomplete';
import {useGoogleMapContext} from './context';
import {useNavigate} from 'react-router-dom';
import PropertyTypeSelectionModal from 'src/components/Modals/PropertyTypeSelection';
import {TIMEZONE_OPTIONS, DEFAULT_TIMEZONE} from 'src/constants/timezones';

// Add this at the top of your file
declare global {
  interface Window {
    initPlacesAPI: () => void;
  }
}

interface AddApartmentModalProps {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  handleConfirm: VoidFunction;
  initData?: Apartment | null;
  propertyType?: PropertyType; // Add this prop
}

function getInitFormData() {
  return {
    apartmentName: '',
    city: '',
    state: '',
    zipCode: '',
    street: '',
    description: '',
    mobile: '',
    timeZone: DEFAULT_TIMEZONE,
  };
}

// Fix the validation function to always return boolean
function validatePhoneNumber(phone: string): boolean {
  if (!phone) return true;
  const cleaned = phone.replace(/\D/g, '');
  return cleaned.length === 9 || cleaned.length === 10;
}

// Add error state for mobile field
function getInitFormErrors() {
  return {
    apartmentName: false,
    mobile: false,
    description: false,
  };
}

function formatPhoneNumber(phone: string): string {
  const cleaned = phone.replace(/\D/g, '');
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  if (cleaned.length === 9) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // 其他长度直接返回原始输入
  return phone;
}

export default function AddApartmentModal({
  initData,
  open,
  setOpen,
  handleConfirm,
  propertyType = PropertyType.MULTI_FAMILY, // Default to multi-family
}: AddApartmentModalProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const account = useAppSelector(selectAccount);
  const [formData, setFormData] = useState<ReturnType<typeof getInitFormData>>(
    initData
      ? {
          apartmentName: initData.name as string,
          city: initData.city as string,
          state: initData.state as string,
          zipCode: initData.zipCode as string,
          street: initData.street as string,
          description: initData.description as string,
          mobile: initData.mobile as string,
          timeZone: initData.timeZone || DEFAULT_TIMEZONE,
        }
      : getInitFormData()
  );

  // Add error state
  const [formErrors, setFormErrors] = useState(getInitFormErrors());

  const googleMapContext = useGoogleMapContext();
  const [position, setPosition] = useState<GeoLocation | null>(
    initData?.location ? initData.location : null
  );
  const [geocode, setGeocode] = useState<any>(googleMapContext?.geocode);
  const [places, setPlaces] = useState<any>(googleMapContext?.places);
  const [mapsLoaded, setMapsLoaded] = useState(false);

  useEffect(() => {
    if (googleMapContext?.geocode && !geocode) {
      setGeocode(googleMapContext.geocode);
    }
    if (googleMapContext?.places && !places) {
      setPlaces(googleMapContext.places);
    }
    if (googleMapContext?.geocode && googleMapContext?.places) {
      setMapsLoaded(true);
    }
  }, [googleMapContext, places, geocode]);

  function handleGoogleLoaded(maps: any) {
    if (maps) {
      setGeocode(new maps.Geocoder());
      setPlaces(maps.places);
      setMapsLoaded(true);
    }
  }

  const handleAddApartment = () => {
    // Validate form before submission
    const errors = {
      apartmentName: !formData.apartmentName.trim(),
      mobile: formData.mobile ? !validatePhoneNumber(formData.mobile) : false,
      description: !formData.description.trim(),
    };

    setFormErrors(errors);

    if (errors.apartmentName || errors.mobile || errors.description) {
      message.error('Please fix the form errors before submitting');
      return;
    }

    setLoading(true);
    if (account?.userDetail?.organizations && account?.userDetail?.organizations[0].id) {
      const formattedMobile = formData.mobile ? formatPhoneNumber(formData.mobile) : '';
      const apartmentData: Apartment = {
        name: formData.apartmentName,
        description: formData.description,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        street: formData.street,
        mobile: formattedMobile,
        location: position || undefined, // Fix: convert null to undefined
        orgId: account.userDetail?.organizations[0].id as string,
        propertyType: propertyType, // Change from property_type to propertyType
        timeZone: formData.timeZone,
      };

      apartmentServiceCreate({apartment: apartmentData})
        .then(res => {
          // Fix: remove type annotation, use destructuring from res.data
          const {id} = res.data as IdRsp;
          handleConfirm();
          message.success('Apartment created successfully');
        })
        .catch(err => {
          message.error(err.message);
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  const handleEditApartment = () => {
    // Validate form before submission
    const errors = {
      apartmentName: !formData.apartmentName.trim(),
      mobile: formData.mobile ? !validatePhoneNumber(formData.mobile) : false,
      description: !formData.description.trim(),
    };

    setFormErrors(errors);

    if (errors.apartmentName || errors.mobile || errors.description) {
      message.error('Please fix the form errors before submitting');
      return;
    }

    if (initData) {
      setLoading(true);
      const formattedMobile = formData.mobile ? formatPhoneNumber(formData.mobile) : '';
      const apartmentData: Apartment = {
        ...initData,
        name: formData.apartmentName,
        description: formData.description,
        city: formData.city,
        state: formData.state,
        zipCode: formData.zipCode,
        street: formData.street,
        mobile: formattedMobile,
        location: position || undefined, // Fix: convert null to undefined
        propertyType: initData?.propertyType || propertyType, // Change from property_type to propertyType
        timeZone: formData.timeZone,
      };

      apartmentServiceUpdate(initData.id as string, {apartment: apartmentData})
        .then(() => {
          handleConfirm();
          message.success('update successfully');
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  async function updateMap(place: PlaceType) {
    if (geocode && place) {
      try {
        await new Promise((resolve, reject) => {
          // Use placeId -- far more reliable than an address string, especially
          // for non-English or mixed-language place descriptions.
          geocode.geocode(
            {placeId: place.place_id},
            (results: any, status: any) => {
              console.debug(results, status);
              if (
                status === 'OK' &&
                results &&
                results[0] &&
                results[0].geometry &&
                results[0].geometry.location
              ) {
                const geometry = results[0].geometry.location;
                const lat = Number(geometry.lat());
                const lng = Number(geometry.lng());
                setPosition({latitude: lat, longitude: lng});
                // Auto-fill timezone from coordinates; user can still override.
                try {
                  const tz = tzlookup(lat, lng);
                  if (tz) {
                    setFormData(prev => ({...prev, timeZone: tz}));
                  }
                } catch {
                  // tz-lookup throws for invalid coords -- keep current value.
                }
                resolve(results);
              } else {
                reject(status);
              }
            }
          );
        });
      } catch (e) {
        console.warn('updateMap geocode failed:', e);
      }
    }
  }

  function handleChange(key: any) {
    return function (event: any) {
      const value = event.target.value;
      setFormData({
        ...formData,
        [key]: value,
      });

      // Clear error when user starts typing
      if (key === 'mobile' && formErrors.mobile) {
        setFormErrors({
          ...formErrors,
          mobile: false,
        });
      }
      if (key === 'apartmentName' && formErrors.apartmentName) {
        setFormErrors({
          ...formErrors,
          apartmentName: false,
        });
      }
      if (key === 'description' && formErrors.description) {
        setFormErrors({
          ...formErrors,
          description: false,
        });
      }
    };
  }

  function handlePlaceSelect(place: PlaceType | null) {
    if (place) {
      updateMap(place);

      // First, extract the main address line for description
      const address = place.structured_formatting.main_text;

      // Initialize variables
      let street = '';
      let city = '';
      let state = '';
      let zipCode = '';

      try {
        // Get the geocoded address components if available
        if (geocode) {
          // Use placeId -- reliable regardless of the place description's language.
          geocode.geocode({placeId: place.place_id}, (results: any, status: any) => {
            if (status === 'OK' && results && results[0] && results[0].address_components) {
              // Extract address components using Google's address_components
              for (const component of results[0].address_components) {
                const types = component.types;

                if (types.includes('street_number')) {
                  street = component.long_name + ' ' + street;
                }
                if (types.includes('route')) {
                  street = street + component.long_name;
                }
                if (types.includes('locality')) {
                  city = component.long_name;
                }
                if (types.includes('administrative_area_level_1')) {
                  state = component.short_name;
                }
                if (types.includes('postal_code')) {
                  zipCode = component.long_name;
                }
              }

              // Also resolve the timezone from this geocode result -- this
              // path is more reliable than updateMap's address-string concat.
              let resolvedTz: string | undefined;
              try {
                const geom = results[0].geometry?.location;
                if (geom) {
                  const lat = typeof geom.lat === 'function' ? geom.lat() : geom.lat;
                  const lng = typeof geom.lng === 'function' ? geom.lng() : geom.lng;
                  if (lat != null && lng != null) {
                    const tz = tzlookup(Number(lat), Number(lng));
                    if (tz) resolvedTz = tz;
                    // Keep position in sync in case updateMap's geocode failed.
                    setPosition({latitude: Number(lat), longitude: Number(lng)});
                  }
                }
              } catch {
                // tz-lookup throws for out-of-range coords -- ignore.
              }

              // Update the form data with extracted components.
              // Use functional setState so we don't clobber the timezone that
              // updateMap() may have set in parallel.
              setFormData(prev => ({
                ...prev,
                description: address,
                street: street,
                city: city,
                state: state,
                zipCode: zipCode,
                ...(resolvedTz ? {timeZone: resolvedTz} : {}),
              }));
              // Clear address error when place is selected
              if (formErrors.description) {
                setFormErrors({
                  ...formErrors,
                  description: false,
                });
              }
            }
          });
        } else {
          // Fallback parsing from place terms if geocoder isn't available

          // For US addresses: try to identify patterns like "Chino Hills, CA"
          const secondaryText = place.structured_formatting.secondary_text;
          const parts = secondaryText.split(',').map(part => part.trim());

          // Specifically for US addresses:
          // Usually format is: [street], [city], [state] [zip]
          if (parts.length >= 2) {
            // First part is likely the city
            city = parts[0];

            // Last part may contain state and zip
            const lastPart = parts[parts.length - 1];
            const stateZipMatch = lastPart.match(
              /([A-Z]{2})(?:\s+(\d{5}(?:-\d{4})?))?\s*,?\s*(.+)?/
            );

            if (stateZipMatch) {
              state = stateZipMatch[1];
              zipCode = stateZipMatch[2] || '';
            }

            // Extract street from main text (it's often the street address)
            street = place.structured_formatting.main_text;
          }
          // Update form data with what we parsed.
          setFormData(prev => ({
            ...prev,
            description: address,
            street: street,
            city: city,
            state: state,
            zipCode: zipCode,
          }));
          // Clear address error when place is selected
          if (formErrors.description) {
            setFormErrors({
              ...formErrors,
              description: false,
            });
          }
        }
      } catch (error) {
        // Still update with whatever we have.
        setFormData(prev => ({
          ...prev,
          description: address,
        }));
        // Clear address error when place is selected
        if (formErrors.description) {
          setFormErrors({
            ...formErrors,
            description: false,
          });
        }
      }
    }
  }

  return (
    <Modal
      loading={loading}
      open={open}
      title={initData ? 'Edit Property' : 'Add Property'}
      handleCancel={() => {
        setOpen(false);
      }}
      handleConfirm={initData ? handleEditApartment : handleAddApartment}
      modalContent={
        <Box component="form">
          {/* Property Name */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Property name
          </Typography>
          <TextField
            fullWidth
            id="apartmentName"
            placeholder="Property name"
            variant="outlined"
            size="small"
            value={formData.apartmentName}
            onChange={handleChange('apartmentName')}
            error={formErrors.apartmentName}
            helperText={formErrors.apartmentName ? 'Property name is required' : ''}
            sx={{mb: 3}}
          />

          {/* Address */}
          <Typography variant="h6" sx={{mb: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Address <span style={{color: 'inherit'}}>*</span>
          </Typography>
          <PlacesAutocomplete
            onSelect={handlePlaceSelect}
            initialValue={formData.description}
            placesAPIs={places}
            inputComponent={params => (
              <TextField
                {...params}
                fullWidth
                size="small"
                placeholder="Address"
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: 'nope',
                }}
                name="location"
                value={formData.description}
                onChange={e => {
                  params.onChange?.(e);
                  handleChange('description')(e);
                }}
                error={formErrors.description}
                helperText={formErrors.description ? 'Address is required' : ''}
              />
            )}
          />

          {/* Optional: Uncomment if you need the additional address fields */}
          {/* <Grid container spacing={2} sx={{ mt: 2 }}>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                id="city"
                placeholder="City"
                variant="outlined"
                value={formData.city}
                onChange={handleChange('city')}
              />
            </Grid>
            <Grid item xs={4}>
              <StateSelect
                size="small"
                initVal={formData.state}
                onChange={state => {
                  setFormData({
                    ...formData,
                    state,
                  });
                }}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField
                fullWidth
                size="small"
                id="zipCode"
                placeholder="Zip Code"
                variant="outlined"
                value={formData.zipCode}
                onChange={handleChange('zipCode')}
              />
            </Grid>
          </Grid> */}

          {/* Contact Phone */}
          <Typography variant="h6" sx={{mb: 1, mt: 3, fontWeight: 400, fontSize: '1.1rem'}}>
            Contact Phone (Optional)
          </Typography>
          <TextField
            fullWidth
            id="mobile"
            placeholder="Contact phone number (e.g., 123-456-7890 or 123-456-789)"
            variant="outlined"
            size="small"
            value={formData.mobile}
            onChange={handleChange('mobile')}
            error={formErrors.mobile}
            helperText={
              formErrors.mobile
                ? 'Please enter a valid 9 or 10 digit phone number'
                : 'Enter phone number'
            }
            sx={{mb: 3}}
          />

          {/* Time Zone -- auto-filled from the selected address; user can override */}
          <Typography variant="h6" sx={{mb: 1, mt: 1, fontWeight: 400, fontSize: '1.1rem'}}>
            Time Zone
          </Typography>
          <FormControl fullWidth sx={{mb: 3}}>
            <InputLabel id="apartment-timezone-label">Time Zone</InputLabel>
            <Select
              labelId="apartment-timezone-label"
              id="apartment-timezone"
              value={formData.timeZone}
              label="Time Zone"
              onChange={e =>
                setFormData(prev => ({...prev, timeZone: e.target.value as string}))
              }
            >
              {TIMEZONE_OPTIONS.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      }
    />
  );
}
